import * as Sentry from '@sentry/react';
import {
  EventType,
  InteractionRequiredAuthError,
  PromptValue,
  PublicClientApplication,
  type EventMessage,
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { NuqsAdapter } from 'nuqs/adapters/react-router';
import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { logger } from './utils/logger';

import App from './App.tsx';
import { loginRequest, msalConfig } from './auth/authConfig';
import './index.css';
import { DEMO_BASENAME, IS_DEMO } from './demo/demoMode';
import ApolloProviderWithClient from './modules/app/ApolloWrapper';

// Loaded only under the demo path, so the app proper never ships the demo data.
const DemoRoot = lazy(() => import('./demo/DemoRoot'));

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: import.meta.env.MODE === 'development' ? 1.0 : 0.2,
});

interface IAuthRedirectRecovery {
  claims?: string;
  prompt?: string;
  reason: string;
}

// Prevents multiple concurrent loginRedirect calls when several Apollo auth
// links simultaneously detect the same auth failure.
let isHandlingAuthRedirect = false;

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

const getAuthErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  return typeof error === 'string' ? error : '';
};

const hasAuthErrorMarker = (
  error: InteractionRequiredAuthError,
  marker: string
) =>
  error.errorCode === marker ||
  error.subError === marker ||
  error.errorNo === marker ||
  getAuthErrorMessage(error).includes(marker);

const isStaleSessionError = (error: unknown) => {
  const message = getAuthErrorMessage(error);
  const lowerMessage = message.toLowerCase();

  return (
    message.includes('AADSTS160021') ||
    (lowerMessage.includes('interaction_required') &&
      lowerMessage.includes('session'))
  );
};

const getSilentAuthRedirectRecovery = (
  event: EventMessage
): IAuthRedirectRecovery | undefined => {
  if (
    event.interactionType !== 'silent' ||
    !(event.error instanceof InteractionRequiredAuthError)
  ) {
    return undefined;
  }

  const error = event.error;

  if (isStaleSessionError(error)) {
    return {
      claims: error.claims || undefined,
      prompt: PromptValue.LOGIN,
      reason: 'stale Azure AD session',
    };
  }

  if (
    hasAuthErrorMarker(error, 'consent_required') ||
    hasAuthErrorMarker(error, 'AADSTS65001')
  ) {
    return {
      claims: error.claims || undefined,
      prompt: PromptValue.CONSENT,
      reason: 'user consent required for the TF-Pulse API scope',
    };
  }

  return {
    claims: error.claims || undefined,
    reason: 'interactive token acquisition required',
  };
};

// Add event callback to handle authentication results
msalInstance.addEventCallback((event) => {
  logger.debug('🔔 MSAL Event:', event);

  if (event.eventType === EventType.LOGIN_SUCCESS) {
    logger.debug('✅ Login Success:', event);
    // The login was successful, account should be available
    const accounts = msalInstance.getAllAccounts();
    logger.debug('📋 Accounts after login:', accounts);

    // Set the active account
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
    // Note: Post-login redirect is handled in handleRedirectPromise below
    // to avoid double redirects
  }

  if (event.eventType === EventType.LOGIN_FAILURE) {
    logger.error('❌ Login Failed:', event);
  }

  if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
    logger.debug('🎫 Token Acquired:', event);
  }

  if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
    logger.error('❌ Token Acquisition Failed:', event);

    // Centralized silent-token recovery. Apollo may have several operations
    // fail at once; handling redirects here lets us serialize one interactive
    // recovery instead of racing per-operation auth links.
    const recovery = getSilentAuthRedirectRecovery(event);

    if (recovery && !isHandlingAuthRedirect) {
      isHandlingAuthRedirect = true;
      const redirectRequest = {
        ...loginRequest,
        ...(recovery.claims ? { claims: recovery.claims } : {}),
        ...(recovery.prompt ? { prompt: recovery.prompt } : {}),
      };

      logger.warn(`⚠️ ${recovery.reason}. Starting interactive auth...`);
      msalInstance.loginRedirect(redirectRequest).catch((err) => {
        logger.error('❌ loginRedirect failed during auth recovery:', err);
        isHandlingAuthRedirect = false;
      });
    }
  }
});

// This will update account state if a user signs in from another tab or window
msalInstance.enableAccountStorageEvents();

const renderApp = () =>
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <BrowserRouter basename={IS_DEMO ? DEMO_BASENAME : undefined}>
        <NuqsAdapter>
          <MsalProvider instance={msalInstance}>
            {IS_DEMO ? (
              <Suspense fallback={null}>
                <DemoRoot />
              </Suspense>
            ) : (
              <ApolloProviderWithClient>
                <App />
              </ApolloProviderWithClient>
            )}
          </MsalProvider>
        </NuqsAdapter>
      </BrowserRouter>
    </StrictMode>
  );

// Initialize MSAL instance and render app only after initialization completes
msalInstance
  .initialize()
  .then(() => {
    logger.debug('🚀 MSAL Initialized');

    // Handle redirect promise (this is called after returning from Azure AD login)
    return msalInstance.handleRedirectPromise();
  })
  .then((response) => {
    logger.debug('🔄 Redirect Response:', response);

    // Always set the active account from the cache, regardless of whether this
    // is a fresh redirect response or a normal page load.
    // With navigateToLoginRequestUrl: true, MSAL may navigate internally and
    // return null even after a successful login, so we cannot rely on response
    // being non-null to know that an account is available.
    const allAccounts = msalInstance.getAllAccounts();
    logger.debug('📋 Accounts:', allAccounts);
    const activeAccount = response?.account ?? allAccounts[0];
    if (activeAccount) {
      msalInstance.setActiveAccount(activeAccount);
      logger.debug('✅ Active account set:', activeAccount.username);
    }

    if (response) {
      logger.debug('✅ Authentication successful after redirect');
    } else {
      logger.debug(
        'ℹ️ No authentication response (normal page load or MSAL handled navigation internally)'
      );
    }

    // Only render the app AFTER MSAL is fully initialized
    renderApp();
  })
  .catch(async (error) => {
    logger.error('❌ MSAL Initialization/Redirect Error:', error);

    // AADSTS160021: Azure AD returned an error on the redirect itself — the
    // cached refresh token / session no longer exists server-side.  Re-using the
    // same session hint would cause another redirect to fail the same way,
    // creating an infinite loop.  Clear the stale cache and force a fresh login
    // without any session hint so Azure AD prompts for credentials.
    if (isStaleSessionError(error)) {
      logger.warn(
        '⚠️ Stale Azure AD session detected. Clearing cache and forcing fresh login.'
      );
      await msalInstance.clearCache();
      await msalInstance.loginRedirect({ ...loginRequest, prompt: 'login' });
      return; // loginRedirect navigates away; nothing more to render
    }

    // For any other error, still render the app so users see something
    renderApp();
  });
